<script setup>
import { watch } from 'vue';
const props = defineProps({ modelValue: { type: Object, required: true }, timed: Boolean });
const emit = defineEmits(['update:modelValue']);
const update = (field, value) => emit('update:modelValue', { ...props.modelValue, [field]: value });
const counts = [
  ['number_questions', 'إجمالي الأسئلة', 1],
  ['number_easy_questions', 'الأسئلة السهلة', 0],
  ['number_medium_questions', 'الأسئلة المتوسطة', 0],
  ['number_hard_questions', 'الأسئلة الصعبة', 0],
];
const revealOptions = [
  ['immediate', 'فور الإجابة (إظهار الإجابة مباشرة)'],
  ['after_submission', 'بعد تسليم الطالب للامتحان'],
  ['after_exam_end', 'بعد انتهاء الوقت العام للامتحان'],
  ['never', 'عدم عرض الإجابات الصحيحة أو النتيجة'],
];
watch(() => props.timed, (timed) => {
  if (!timed) emit('update:modelValue', { ...props.modelValue, personal_duration_minutes: null,
    answer_reveal_timing: props.modelValue.answer_reveal_timing === 'after_exam_end' ? 'after_submission' : props.modelValue.answer_reveal_timing });
});
</script>

<template>
  <div class="form-card exam-enhancements" dir="rtl">
    <div class="form-card__header"><span class="form-card__title">تكوين الامتحان وسلوك الإجابات</span></div>
    <div class="form-card__body">
      <fieldset class="form-field">
        <legend class="field-label">نوع الامتحان</legend>
        <div class="type-selector">
          <label v-for="option in [[false, 'يدوي'], [true, 'آلي (Automated Exam)']]" :key="String(option[0])"
            :class="['type-selector__item', { active: modelValue.automated === option[0] }]">
            <input type="radio" name="exam-generation" :checked="modelValue.automated === option[0]"
              @change="update('automated', option[0])" /> {{ option[1] }}
          </label>
        </div>
      </fieldset>
      <div v-if="modelValue.automated" class="mt-3" data-test="automated-counts">
        <p>تُختار أسئلة صالحة عشوائياً من الموقع المحدد في المنهج، وتُحفظ كأَسئلة الامتحان دون تكرار. ترتيب عرضها للطلاب تحدده إعدادات الترتيب.</p>
        <div class="row g-3">
          <div v-for="[field, label, minimum] in counts" :key="field" class="col-md-3 col-6 form-field">
            <label :for="field" class="field-label">{{ label }}</label>
            <input :id="field" type="number" :min="minimum" max="1000" step="1" required class="form-control"
              :value="modelValue[field]" @input="update(field, $event.target.value === '' ? null : Number($event.target.value))" />
          </div>
        </div>
        <p class="field-error" v-if="modelValue.number_questions !== modelValue.number_easy_questions + modelValue.number_medium_questions + modelValue.number_hard_questions">
          مجموع السهل والمتوسط والصعب يجب أن يساوي العدد الكلي.
        </p>
      </div>

      <fieldset class="form-field mt-4">
        <legend class="field-label">تغيير الإجابة</legend>
        <div class="type-selector">
          <label v-for="option in [[true, 'السماح بتغيير الإجابة'], [false, 'تثبيت الإجابة بعد اختيارها']]" :key="String(option[0])"
            :class="['type-selector__item', { active: modelValue.allow_answer_changes === option[0] }]">
            <input type="radio" name="answer-changing" :checked="modelValue.allow_answer_changes === option[0]"
              @change="update('allow_answer_changes', option[0])" /> {{ option[1] }}
          </label>
        </div>
        <small>تظل قواعد التنقل بين الأسئلة مطبقة في الحالتين.</small>
      </fieldset>

      <div class="form-field mt-4">
        <label for="answer-reveal-timing" class="field-label">موعد إظهار الإجابات والنتيجة</label>
        <select id="answer-reveal-timing" class="form-select" :value="modelValue.answer_reveal_timing"
          @change="update('answer_reveal_timing', $event.target.value)">
          <option v-for="[value, label] in revealOptions" :key="value" :value="value"
            :disabled="value === 'after_exam_end' && !timed">{{ label }}</option>
        </select>
        <small v-if="modelValue.answer_reveal_timing === 'immediate'">بعد حفظ الإجابة يظهر الصحيح بالأخضر والخطأ بالأحمر، مع احترام إعداد تغيير الإجابة.</small>
        <small v-else-if="modelValue.answer_reveal_timing === 'after_exam_end'">ينتظر الطالب نهاية الوقت العام، حتى لو انتهت مدته الشخصية أو سلّم مبكراً.</small>
        <small v-else-if="modelValue.answer_reveal_timing === 'never'">تظل الإجابات والتصحيح متاحة للإدارة فقط.</small>
      </div>

      <div class="form-field mt-4" v-if="timed">
        <label class="field-label">
          <input type="checkbox" :checked="modelValue.personal_duration_minutes !== null"
            @change="update('personal_duration_minutes', $event.target.checked ? 30 : null)" />
          مدة شخصية لكل طالب
        </label>
        <div v-if="modelValue.personal_duration_minutes !== null">
          <label for="personal-duration" class="field-label">المدة بالدقائق</label>
          <input id="personal-duration" class="form-control" type="number" min="1" max="1440" step="1" required
            :value="modelValue.personal_duration_minutes"
            @input="update('personal_duration_minutes', $event.target.value === '' ? 0 : Number($event.target.value))" />
          <small>تبدأ من وقت دخول الطالب المسجل على الخادم، ولا تتجاوز وقت نهاية الامتحان العام. إعادة التحميل لا تعيد ضبطها.</small>
        </div>
        <small v-else>بدون مدة شخصية، ينتهي الامتحان للجميع في الوقت العام المحدد.</small>
      </div>
    </div>
  </div>
</template>

<style scoped>
fieldset { border: 0; padding: 0; }
legend { float: none; font-size: inherit; width: auto; }
small { display: block; margin-top: .5rem; color: var(--text-secondary); }
input[type="radio"], input[type="checkbox"] { margin-inline-end: .5rem; }
</style>

